import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import SPKAddPositionStep, {
  SPKAddPositionStepStatus,
} from './SPKAddPositionStep';
import SPKPositionOverviewStep from './SPKPositionOverviewStep/SPKPositionOverviewStep';
import SPKJobDescriptionStep from './SPKJobDescriptionStep/SPKJobDescriptionStep';
import SPKAdditionalInformation from './SPKAdditionalInformation/SPKAdditionalInformation';
import SPKAddPositionCompleted from './SPKAddPositionCompleted/SPKAddPositionComplete';
import SPKFirstRoundInterview from './SPKFirstRoundInterview/SPKFirstRoundInterview';
import SPKApplicationRequirements from './SPKApplicationRequirements/SPKApplicationRequirements';
import { SIMERA_POSITIONS_ROUTE } from '../../constants/routes';

enum SPKAddPositionPages {
  AddPositionForm,
  Completed,
  FirstRoundInterview,
}
const SPKAddPosition = ({ isInternal = false }: { isInternal?: boolean }) => {
  const [t] = useTranslation('global');
  const navigate = useNavigate();
  const [activePage, setActivePage] = useState<SPKAddPositionPages>(
    SPKAddPositionPages.AddPositionForm
  );

  const [pageTabs, setPageTabs] = useState(() => [
    {
      title: t('EmployerAddPosition.PositionOverview'),
      status: SPKAddPositionStepStatus.inProgress,
    },
    {
      title: t('EmployerAddPosition.JobDescription'),
      status: SPKAddPositionStepStatus.pending,
    },
    {
      title: t('EmployerAddPosition.AdditionalInformation'),
      status: SPKAddPositionStepStatus.pending,
    },
    ...(isInternal
      ? [
          {
            title: t('EmployerAddPosition.ApplicationRequirements'),
            status: SPKAddPositionStepStatus.pending,
          },
        ]
      : []),
  ]);

  const handleOnComplete = () => {
    setPageTabs((prev) => {
      let previousWasInProgress = false;
      return [...prev].map((singleTab) => {
        if (previousWasInProgress) {
          previousWasInProgress = false;
          singleTab.status = SPKAddPositionStepStatus.inProgress;
        } else if (singleTab.status === SPKAddPositionStepStatus.inProgress) {
          previousWasInProgress = true;
          singleTab.status = SPKAddPositionStepStatus.completed;
        }
        return singleTab;
      });
    });
  };

  const handleOnGoBack = () => {
    setPageTabs((prev) => {
      const tempPageTabs = [...prev];
      const inProgressIndex = tempPageTabs.findIndex(
        (item) => item.status === SPKAddPositionStepStatus.inProgress
      );
      if (inProgressIndex === 1) {
        tempPageTabs[0].status = SPKAddPositionStepStatus.inProgress;
      } else {
        tempPageTabs[inProgressIndex - 1].status =
          SPKAddPositionStepStatus.inProgress;
      }
      tempPageTabs[inProgressIndex].status = SPKAddPositionStepStatus.pending;

      return tempPageTabs;
    });
  };

  const showTabPage = useMemo(() => {
    const activeTab =
      pageTabs.find(
        (singleTab) => singleTab.status === SPKAddPositionStepStatus.inProgress
      ) || pageTabs[0];
    switch (activeTab?.title) {
      case pageTabs[3]?.title:
        return (
          <SPKApplicationRequirements
            onGoBack={handleOnGoBack}
            onSuccess={() => navigate(SIMERA_POSITIONS_ROUTE)}
          />
        );
      case pageTabs[2].title:
        return (
          <SPKAdditionalInformation
            onComplete={
              isInternal
                ? handleOnComplete
                : () => setActivePage(SPKAddPositionPages.Completed)
            }
            onGoBack={handleOnGoBack}
            isInternal={isInternal}
          />
        );
      case pageTabs[1].title:
        return (
          <SPKJobDescriptionStep
            onComplete={handleOnComplete}
            onGoBack={handleOnGoBack}
          />
        );
      case pageTabs[0].title:
      default:
        return (
          <SPKPositionOverviewStep
            onComplete={handleOnComplete}
            isInternal={isInternal}
          />
        );
    }
  }, [pageTabs, isInternal, navigate]);

  return (
    <section className="flex flex-col items-center text-spk-highContrast font-mulish pb-8 max-w-2xl mx-auto w-full">
      {activePage === SPKAddPositionPages.AddPositionForm && (
        <>
          <div className="flex flex-row gap-8 mx-auto my-8 w-full">
            {pageTabs.map((singleTab) => (
              <SPKAddPositionStep
                title={singleTab.title}
                status={singleTab.status}
                key={`step_${singleTab.title}`}
              />
            ))}
          </div>
          {showTabPage}
        </>
      )}
      {activePage === SPKAddPositionPages.Completed && (
        <SPKAddPositionCompleted
          onComplete={() =>
            setActivePage(SPKAddPositionPages.FirstRoundInterview)
          }
        />
      )}

      {activePage === SPKAddPositionPages.FirstRoundInterview && (
        <SPKFirstRoundInterview />
      )}
    </section>
  );
};

SPKAddPosition.defaultProps = {
  isInternal: false,
};

export default SPKAddPosition;
